import type {
  ContentFeedCommentCreateRequest,
  ContentFeedCommentCreateResponse,
  ContentFeedCommentListResponse,
} from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import { contentFeedCommentCreateRequestSchema } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { ErrorResponse } from '@sola/shared-utils/httpClient.ts';
import {
  httpGetWithStatus,
  httpPostWithStatus,
  isErrorResponse,
  isSuccessStatus,
} from '@sola/shared-utils/httpClient.ts';

import { MY_API_BASE } from '../constants.ts';

// Trailing slash: same CloudFront OAC / trailingSlash reason as other routes.
export const routeContentFeedComments = () =>
  `${MY_API_BASE}/content-feed/comments/`;

/**
 * Creates a comment on a content feed entry or replies to an existing comment.
 * POST /api/content-feed/comments/
 *
 * The request body is validated against the shared schema before sending.
 * Exactly one of entryId or parentId must be provided.
 */
export const createComment = async (
  entryId: string | undefined,
  parentId: string | undefined,
  text: string,
  locale = 'en',
): Promise<ContentFeedCommentCreateResponse> => {
  const body: Partial<ContentFeedCommentCreateRequest> = {
    locale,
    text,
  };

  if (entryId) {
    body.entryId = entryId;
  }
  if (parentId) {
    body.parentId = parentId;
  }

  const parsed = contentFeedCommentCreateRequestSchema.safeParse(body);
  if (!parsed.success) {
    throw new Error(
      `Invalid comment request: ${parsed.error.issues.map((i) => i.message).join(', ')}`,
    );
  }

  const { response, statusCode } = await httpPostWithStatus<
    ContentFeedCommentCreateResponse | ErrorResponse
  >(routeContentFeedComments(), parsed.data, {
    signRequest: true,
  });

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error(`Comment create failed: ${statusCode}`);
  }

  return response;
};

/**
 * Fetches paginated comments for a content feed entry.
 * GET /api/content-feed/comments/?entryId=...&locale=...
 */
export const fetchComments = async (params: {
  entryId: string;
  locale?: string;
  limit?: number;
  cursor?: string;
}): Promise<ContentFeedCommentListResponse> => {
  const searchParams = new URLSearchParams({
    entryId: params.entryId,
    locale: params.locale || 'en',
  });

  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }

  const url = `${routeContentFeedComments()}?${searchParams.toString()}`;

  const { response, statusCode } = await httpGetWithStatus<
    ContentFeedCommentListResponse | ErrorResponse
  >(url, {
    signRequest: true,
  });

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error(`Comment list failed: ${statusCode}`);
  }

  return response;
};
